import React, { ChangeEvent, MouseEvent, ReactNode, RefObject, useContext, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';

import ErrorContext from '../../_common/Controller/errorContext';

import {
  StyledSelectableListOption,
  SelectableListControl,
  SelectableListBody,
  SelectableListRadio,
  SelectableListCheckbox,
} from '../styles';

export interface SelectableListOptionProps {
  id: string;
  name: string;
  value?: string | number | boolean;
  content?: ReactNode;
  isChecked?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isHorizontal?: boolean;
  onFocus?: () => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  multiSelect?: boolean;
  externalRef?: RefObject<HTMLInputElement>;
  'data-testid'?: string | number;
  'data-track-name'?: string;
  'data-track-events'?: string[];
  'data-track-payload'?: any;
}

function SelectableListOption(props: SelectableListOptionProps) {
  const {
    id,
    name,
    value,
    content,
    onChange,
    onClick,
    autoFocus,
    isChecked,
    isInvalid,
    isDisabled,
    externalRef,
    'data-testid': dataTestId,
  } = props;

  const [checked, setChecked] = React.useState(value);
  const errorContext = useContext(ErrorContext);

  useEffect(() => {
    if (props.multiSelect && typeof props.value !== 'boolean') {
      throw new Error('`multiSelect` SelectableList should have boolean values.');
    }
  }, [props.multiSelect, props.value]);

  const handleChange = e => {
    setChecked(e.target.checked);
    onChange && onChange(e);
  };

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        selectableListOptionChecked: props.multiSelect ? checked : isChecked,
        selectableListOptionInvalid:
          !isDisabled && (isInvalid || (errorContext.hasError && errorContext.errorAutoInvalidate)),
        selectableListOptionDisabled: isDisabled,
      })}
    >
      <StyledSelectableListOption htmlFor={id} data-testid={dataTestId}>
        <SelectableListControl>
          {props.multiSelect ? (
            <SelectableListCheckbox
              id={id}
              width="full"
              checked={checked as boolean | undefined}
              disabled={isDisabled}
              invalid={!isDisabled && isInvalid}
              onChange={handleChange}
              autoFocus={autoFocus}
              externalRef={externalRef}
              data-track-name={props['data-track-name']}
              data-track-payload={props['data-track-payload']}
              data-track-events={props['data-track-events']}
            />
          ) : (
            <SelectableListRadio
              id={id}
              width="full"
              value={value as string}
              disabled={isDisabled}
              name={name}
              checked={isChecked}
              invalid={!isDisabled && isInvalid}
              onChange={onChange}
              onClick={onClick}
              autoFocus={autoFocus}
              externalRef={externalRef}
              data-track-name={props['data-track-name']}
              data-track-payload={props['data-track-payload']}
              data-track-events={props['data-track-events']}
            />
          )}
        </SelectableListControl>
        {content && <SelectableListBody>{content}</SelectableListBody>}
      </StyledSelectableListOption>
    </ThemeProvider>
  );
}

SelectableListOption.defaultProps = {
  content: '',
  value: '',
  isChecked: false,
  isInvalid: false,
  isDisabled: false,
  onChange: () => {},
  onClick: () => {},
  autoFocus: false,
  multiSelect: false,
  'data-track-events': ['change'],
};

export default SelectableListOption;
